import { LEGACY_TENANT_SLUGS, canonicalTenantSlug } from '../api/types';

/**
 * Single source of truth for the mapping between a legacy tenant slug and the
 * `tenant:<slug>` permission spellings that grant it in the Auth0 JWT.
 *
 * The two directions are used in different places and MUST agree — a spelling
 * that grants a tenant in the switcher has to resolve that same tenant when the
 * app boots. They were previously implemented twice (an alias builder in
 * `useAccessibleTenants`, an ad-hoc raw/underscore check in `TenantBootstrap`),
 * so a user whose only grant used a historic spelling saw the tenant in the
 * switcher but was routed to /onboarding on login.
 */

const TENANT_PERMISSION_PREFIX = 'tenant:';

/**
 * The permission spellings that grant `slug`.
 *
 * Note: `seezeit` is intentionally granted by both `tenant:seezeit` AND
 * `tenant:seeizeit` — there's a known typo in the existing Auth0 permission
 * name, and users still carry it. Without the alias, Seezeit is dropped.
 *
 * Slugs with `_v2` are stored as `-v2` in JWT permissions, matching the
 * convention the Auth0 permissions were created with.
 */
export const buildPermissionAliases = (slug: string): string[] => {
    const aliases: string[] = [];
    aliases.push(`${TENANT_PERMISSION_PREFIX}${slug}`);
    if (slug.endsWith('_v2')) {
        aliases.push(`${TENANT_PERMISSION_PREFIX}${slug.replace('_v2', '-v2')}`);
    }
    if (slug === 'seezeit') {
        // Known typo in the legacy permission name.
        aliases.push(`${TENANT_PERMISSION_PREFIX}seeizeit`);
    }
    return aliases;
};

// Permission spelling -> legacy slug, built once from the aliases above so the
// two directions can't drift apart.
const SLUG_BY_PERMISSION = new Map<string, string>();
LEGACY_TENANT_SLUGS.forEach((slug) => {
    buildPermissionAliases(slug).forEach((permission) => {
        SLUG_BY_PERMISSION.set(permission, slug);
    });
});

/**
 * Reverse direction: the legacy tenant slug a single JWT permission grants, or
 * `null` when the permission isn't a tenant grant (e.g. `read:bookings`) or
 * doesn't name a known legacy tenant.
 *
 * Derived from `buildPermissionAliases`, so every alias added for the switcher
 * automatically resolves on app boot too.
 */
export const legacyTenantForPermission = (permission: string): string | null => {
    if (!permission.startsWith(TENANT_PERMISSION_PREFIX)) return null;
    const known = SLUG_BY_PERMISSION.get(permission);
    // Canonicalised on the way out: a grant still written as the retired v1
    // spelling resolves to the current tenant rather than to a slug the
    // switcher no longer offers, which would boot the user onto a tenant they
    // then cannot see in the list (CEO, 2026-09-18). See
    // DEPRECATED_TENANT_SLUGS in `api/types.ts`.
    if (known) return canonicalTenantSlug(known);
    // Fallback for any other spelling that encodes `_` as `-`.
    const underscored = permission
        .slice(TENANT_PERMISSION_PREFIX.length)
        .replace(/-/g, '_');
    return LEGACY_TENANT_SLUGS.has(underscored)
        ? canonicalTenantSlug(underscored)
        : null;
};
